import React, { useEffect, useRef, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import { makeStyles } from '@mui/styles';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from 'chart.js';

const useStyles = makeStyles(() => ({
  Sku_Sto_Chart: {
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  BarCard: {
    borderRight: '1px solid #ddd', // Add border between Bar and Doughnut chart
    paddingRight: '20px', // Add padding for space between border and content
    marginRight: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    flex: '1',
    marginRight: '20px',
    marginBottom: '20px',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
    '&:last-child': {
      marginRight: '0',
    },
  },
  cardHeader: {
    fontSize: '9px',
    color: '#555',
    marginBottom: '5px',
  },
  TextHeader: {
    fontSize: '12px',
    color: 'black',
    marginBottom: '5px',
    fontWeight: 'lighter'
  },
  cardDescription: {
    fontSize: '12px',
    color: 'black',
    marginBottom: '5px',
    fontWeight: 100
  },
  chartWrapper: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '-5vh',
  },
  cardSmall: {
    backgroundColor: '#F1FAFF',
    marginRight: '10vw',
    padding: '10px'
  },
  wowCircle: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'white',
    borderRadius: '50%',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    fontSize: '10px',
    fontWeight: 'bold',
    color: 'black',
    transition: 'all 0.3s ease',
    textAlign: 'center',
  },
  dateRange: {
    fontSize:'x-small',
    marginTop: '-20px'
  }
}));

const Sku_Sto_Chart = ({ data }) => {
  const classes = useStyles();
  const chartRef = useRef(null);
  // const [wowPositions, setWowPositions] = useState([]);
  Chart.register(ChartDataLabels);

  const DOH_yesterdayCheck = data?.["On hand today"]?.[0]?.DOH_yesterday;
  const dohTodayCheck = data?.["On hand today"]?.[0]?.DOH_today;

  // Map the data from the JSON
  // Helper function to safely access nested properties and provide a default value
  const getValue = (obj, path, defaultValue = "N/A") => {
    const value = path.split('.').reduce((acc, key) => acc?.[key], obj) ?? defaultValue;
    // If the value is a number, round it to 2 decimal places
    if (typeof value === 'number') {
      return parseFloat(value.toFixed(2));
    }
    return value;
  };


  // Helper function to format numbers as k or M
  const formatNumber = (value) => {
    if (value === "N/A" || isNaN(value)) { return value;} // Return as-is if not a number or "N/A"

    const num = parseFloat(value);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`; // Convert to millions
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`; // Convert to thousands
    } else {
      return num.toString(); // Return as-is if less than 1000
    }
  };

  // Helper function to parse and format numeric values
  // const parseNumber = (value, defaultValue = 0) => {
  //   const parsed = parseFloat(value);
  //   return isNaN(parsed) ? defaultValue : parsed.toFixed(2);
  // };

  // Extract values from data
  const dohYesterday = DOH_yesterdayCheck ?? 0;
  const dohToday = dohTodayCheck ?? 0;
  const forecastBias = getValue(data, 'Forecast_Bias.0.Forecast_Bias');
  const pastDueSTOS = getValue(data, 'Past due STOS.0.Past_due_STOS');

  // Calculate blocked, restricted, and unrestricted values
  const inventoryData = getValue(data, 'Inventory on hand today.0', {});
  const blockedValue = (inventoryData.Blocked_Pallets ?? inventoryData.Blocked_Cases ?? inventoryData.Blocked_Value) * 100 || "N/A";
  const restrictedValue = (inventoryData.Restricted_Pallets ?? inventoryData.Restricted_Cases ?? inventoryData.Restricted_Value) * 100 || "N/A";
  const unrestrictedValue = (inventoryData.Unrestricted_Pallets ?? inventoryData.Unrestricted_Cases ?? inventoryData.Unrestricted_Value) * 100 || "N/A";

  // Parse and format numeric values with fallback paths
  const natPOOS = formatNumber(
    getValue(data, 'National POOS.0.NatnlCutQtyinCases') !== 'N/A'
      ? getValue(data, 'National POOS.0.NatnlCutQtyinCases') :
      getValue(data, 'National POOS.0.ValueAtNatPOOSRisk') !== 'N/A'
        ? getValue(data, 'National POOS.0.ValueAtNatPOOSRisk') :
        getValue(data, 'National POOS.0.NatnlCutQtyinPL') !== 'N/A'
          ? getValue(data, 'National POOS.0.NatnlCutQtyinPL') :
          "-"
  );

  const regPOOS = formatNumber(
    getValue(data, 'Regional POOS.0.RegCutQtyinCases') !== 'N/A'
      ? getValue(data, 'Regional POOS.0.RegCutQtyinCases')
      : getValue(data, 'Regional POOS.0.ValueAtRegPOOSRisk') !== 'N/A'
        ? getValue(data, 'Regional POOS.0.ValueAtRegPOOSRisk')
        : getValue(data, 'Regional POOS.0.RegCutQtyinPL') !== 'N/A'
          ? getValue(data, 'Regional POOS.0.RegCutQtyinPL')
          : "-"
  );

  const projectedSalvage = formatNumber(
    getValue(data, 'Projected Salvage.0.SalvageCases') !== 'N/A'
      ? getValue(data, 'Projected Salvage.0.SalvageCases')
      : getValue(data, 'Projected Salvage.0.SalvageValue') !== 'N/A'
        ? getValue(data, 'Projected Salvage.0.SalvageValue')
        : getValue(data, 'Projected Salvage.0.SalvagePallets') !== 'N/A'
          ? getValue(data, 'Projected Salvage.0.SalvagePallets')
          : "-"
  );

  const barData = {
    labels: ['Today', 'Yesterday'],
    datasets: [
      {
        label: 'Days On Hand',
        data: [dohToday, dohYesterday],
        backgroundColor: ['#ff69b4', '#0082c3'],
        borderRadius: 50,
      },
    ],
  };

  const barOptions = {
    indexAxis: 'y', // Horizontal bar chart
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: false, // Remove grid lines on the x-axis
        },
        border: {
          display: true, // Keep x-axis border line
        },
      },
      y: {
        grid: {
          display: false, // Remove grid lines on the y-axis
        },
        border: {
          display: false, // Remove y-axis border line
        },
      },
    },
    datasets: {
      bar: {
        barThickness: 15, // Adjust this value to control the bar width
      },
    },
    plugins: {
      legend: { display: false }, // Hide legend
      tooltip: { enabled: false }, // Disable tooltips
      datalabels: {
        anchor: 'end', // Position the label at the end of the bar
        align: 'end',  // Align the label near the bar tip
        color: (context) => {
          // Set the label color based on the corresponding background color
          const index = context.dataIndex;
          return context.dataset.backgroundColor[index];
        },
        font: {
          weight: 'bold',
        },
        formatter: function (value) {
          return value;
        },
      },
    },
  };
  const doughnutData = {
    labels: ['Clear', 'Restricted', 'Blocked'],
    datasets: [
      {
        data: [
          (isNaN(parseFloat(unrestrictedValue)) ? 0 : parseFloat(unrestrictedValue)).toFixed(2),
          (isNaN(parseFloat(restrictedValue)) ? 0 : parseFloat(restrictedValue)).toFixed(2),
          (isNaN(parseFloat(blockedValue)) ? 0 : parseFloat(blockedValue)).toFixed(2),
        ],
        backgroundColor: ['#f79b77', '#0082c3', '#f7bb45'],
      },
    ],
  };


  const formatValue = (value) => {
    if (value >= 1e6) {
      return (value / 1e6).toFixed(1) + 'M'; // Format as millions
    } else if (value >= 1e3) {
      return (value / 1e3).toFixed(1) + 'K'; // Format as thousands
    }
    return value.toString(); // Return the original number if below 1000
  };

  const doughnutOptions = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true, // Ensures point style is used in the legend
          generateLabels: (chart) => {
            const data = chart.data.datasets[0].data;
            return chart.data.labels.map((label, index) => ({
              text: `${label}: ${formatValue(data[index])}`, // Format the value with K/M
              fillStyle: chart.data.datasets[0].backgroundColor[index],
              strokeStyle: chart.data.datasets[0].backgroundColor[index],
              lineWidth: 1,
              hidden: chart.getDatasetMeta(0).data[index].hidden,
              index,
              pointStyle: 'circle', // Ensures the legend item is displayed as a circle
              radius: 6,  // Sets the radius for the circle
            }));
          },
          color: 'black', // Legend text color
          padding: 10,
        },
      },
      tooltip: {
        enabled: false, // Disable tooltips
      },
      datalabels: {
        display: false, // Disable data labels inside the doughnut
      },
    },
  };



  const wowData = [
    {
      label: 'Clear',
      value: (isNaN(parseFloat(data?.["Inventory on hand today"]?.[0]?.Unrestricted_WoW)) ? 0 : parseFloat(data?.["Inventory on hand today"]?.[0]?.Unrestricted_WoW)).toFixed(2),
      color: '#fcfcfa',
    },
    {
      label: 'Restricted',
      value: (isNaN(parseFloat(data?.["Inventory on hand today"]?.[0]?.Restricted_WoW)) ? 0 : parseFloat(data?.["Inventory on hand today"]?.[0]?.Restricted_WoW)).toFixed(2),
      color: '#fcfcfa',
    },
    {
      label: 'Blocked',
      value: (isNaN(parseFloat(data?.["Inventory on hand today"]?.[0]?.Blocked_WoW)) ? 0 : parseFloat(data?.["Inventory on hand today"]?.[0]?.Blocked_WoW)).toFixed(2),
      color: '#fcfcfa',
    },
  ];

  return (
    <div className={classes.Sku_Sto_Chart}>
      {/* Row 1 */}
      <div className={classes.row}>
        <div className={classes.BarCard}>
          <h4 className={classes.cardHeader}>Days On Hand as of today</h4>
          <Bar data={barData} options={barOptions} style={{ height: '20vh', width: '25vw' }} />
        </div>
        <div className=''>
          <h4 className={classes.cardHeader}>Inventory Holding as of today</h4>
          <div className={classes.chartWrapper}>
            <Doughnut ref={chartRef} data={doughnutData} options={doughnutOptions} style={{ width: '200px', height: '200px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {wowData.map((item, index) => (
                <span key={index} style={{ fontSize: '11px', padding: '3px' }}>WOW: {item.value}%</span>
              ))}
            </div>
          </div>
        </div>
        <div >
          <h4 className={classes.TextHeader}>Forecast Bias</h4>
          <h2 className={classes.cardValue} style={{ color: '#00a1dd' }}>
            {forecastBias === 'N/A' ? '0' : parseFloat(forecastBias).toFixed(2) + '%'}
          </h2>
          <p className={classes.cardDescription}>Last 28 Days</p>
          <h4 className={classes.TextHeader}>Past Due STO's</h4>
          <h2 className={classes.cardValue} style={{ color: '#007bff' }}>{pastDueSTOS === 'N/A' ? '0' : Math.floor(pastDueSTOS)}</h2>
          <p className={classes.cardDescription}>Last 28 Days</p>
        </div>
      </div>

      {/* Row 2 */}
      <div className={`${classes.row} ${classes.cardSmall}`}>
        <div className=''>
          <h4 className={classes.TextHeader}>National POOS</h4>
          <h2 className={classes.cardValueSmall} style={{ color: '#007bff' }}>{natPOOS === "null" ? "$0" : natPOOS}</h2>
          <h2 className={classes.dateRange} style={{ color: '#007bff' }}>{getValue(data, 'National POOS.0.NPOOSDateRange', '')}</h2>
          <p className={classes.cardDescription}>Projected this week</p>
        </div>
        <div className=''>
          <h4 className={classes.TextHeader}>Regional POOS</h4>
          <h2 className={classes.cardValueSmall} style={{ color: '#007bff' }}>{regPOOS === "null" ? "$0" : regPOOS}</h2>
          <h2 className={classes.dateRange} style={{ color: '#007bff' }}>{getValue(data, 'Regional POOS.0.RPOOSDateRange', '')}</h2>
          <p className={classes.cardDescription}>Projected this week</p>
        </div>
        <div className=''>
          <h4 className={classes.TextHeader}>Projected Salvage</h4>
          <h2 className={classes.cardValueSmall} style={{ color: '#007bff' }}>{projectedSalvage === "null" ? "$0" : projectedSalvage}</h2>
          <p className={classes.cardDescription}>Projected this week</p>
        </div>
      </div>
    </div>
  );
};

export default Sku_Sto_Chart;
